<template>
  <RfBaseModal ref="connectorErrorModal" title="Details">
    <template #body>
      <v-card-text v-if="errorDetails" class="action-error-table-modal">
        <v-row>
          <v-col class="action-error-col-1">Date:</v-col>
          <v-col class="action-error-col-2">
            {{ dayjs(errorDetails.timestamp * 1000).format("MMM Do YYYY, h:mm a") }}
          </v-col>
        </v-row>
        <v-row>
          <v-col class="action-error-col-1">User ID:</v-col>
          <v-col>{{ errorDetails.parsed_message.user_id }}</v-col>
        </v-row>
        <v-row>
          <v-col class="action-error-col-1">Error:</v-col>
          <v-col v-if="individualErrors || clientAction" class="action-error-col-2">
            {{ errorDetails.error }}
          </v-col>
          <v-col v-else class="action-error-col-2"
            >All actions failed with message: {{ errorDetails.error }}</v-col
          >
        </v-row>
        <v-row v-if="individualErrors">
          <v-col class="action-error-col-1">Note:</v-col>
          <v-col class="action-error-col-2">Actions that succeeded are not included here</v-col>
        </v-row>
        <v-table class="rf-settings-table-data" style="margin-top: 20px">
          <thead>
            <tr>
              <th v-if="clientAction">Action Type</th>
              <th v-else>Connector</th>
              <th v-if="clientAction" style="width: 120px">Action Name</th>
              <th v-else style="width: 120px">Action Type</th>
              <th>Error</th>
            </tr>
          </thead>
          <tbody v-if="individualErrors || clientAction">
            <tr v-for="(error, index) in errorDetails.error_messages" :key="index">
              <td>{{ displayConnectorType(error.message) }}</td>
              <td>{{ error.message.description || error.message.operation }}</td>
              <td>{{ error.error }}</td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr v-for="(item, index) in errorDetails.parsed_message.items" :key="index">
              <div v-if="item">
                <td>{{ displayConnectorType(item) }}</td>
                <td>{{ item.description }}</td>
                <td>{{ errorDetails.error }}</td>
              </div>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
    </template>
    <template #actions>
      <RfButton
        icon="md:clear"
        buttonText="Delete"
        color="error"
        class="px-7"
        :disabled="props.disabled"
        @click="deleteError"
      />
      <RfButton
        v-if="!clientAction"
        icon="md:autorenew"
        buttonText="Retry"
        color="primary"
        class="px-7"
        :disabled="props.disabled"
        @click="retryAndDelete"
      />
      <RfButton buttonText="Close" variant="outlined" class="cancel-btn px-5" @click="close" />
    </template>
  </RfBaseModal>
</template>

<script setup>
import RfButton from "@/components/buttons/RfButton.vue";
import RfBaseModal from "@/components/modals/RfBaseModal.vue";
import { usePromptStore } from "@/pinia/promptStore";
import { useToastsStore } from "@/pinia/toastsStore";
import StringUtils from "@/utils/StringUtils";
import dayjs from "dayjs";
import { computed, ref } from "vue";

const props = defineProps({
  disabled: { type: [Boolean, String], default: false },
  prompt: { type: Object, default: () => ({}) },
});

const toastsStore = useToastsStore();
const promptStore = usePromptStore();

const connectorErrorModal = ref();
const errorDetails = ref(null);

const clientAction = computed(() => errorDetails.value?.parsed_message?.client_action);

const individualErrors = computed(
  () => errorDetails.value?.error_messages?.length && !clientAction.value,
);

const show = error => {
  errorDetails.value = error;
  connectorErrorModal.value.show?.();
};

const close = () => {
  connectorErrorModal.value.close?.();
  errorDetails.value = null;
};

const displayConnectorType = error => (error?.provider ? StringUtils.titleize(error.provider) : "");

const retryAndDelete = async () => {
  await promptStore.retryAndDeleteConnectorError({
    appId: props.prompt.app_id,
    pathId: props.prompt.id,
    connectorError: errorDetails.value,
  });
  await promptStore.getPrompt({
    appId: props.prompt.app_id,
    pathId: props.prompt.id,
  });
  toastsStore.create({ type: "success", body: "Retry Successful" });
  close();
};

const deleteError = async () => {
  await promptStore.deleteConnectorError({
    appId: props.prompt.app_id,
    pathId: props.prompt.id,
    connectorError: errorDetails.value,
  });
  await promptStore.getPrompt({
    appId: props.prompt.app_id,
    pathId: props.prompt.id,
  });
  toastsStore.create({ type: "success", body: "Error deleted" });
  close();
};

defineExpose({ show });
</script>
